import { computed, inject, Injectable, signal } from '@angular/core';
import { GraphService } from '../../auth/services/graph.service';
import { filter, forkJoin, interval, map, Observable, of } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

interface UserCacheData {
    photo: Blob | null;
    userName: string;
    orgCode: string;
}
@Injectable({
    providedIn: 'root'
})
export class UserCacheService {
    private graphService = inject(GraphService);
    private userCache = signal(new Map<string, UserCacheData>());
    private loadingUsers = new Set<string>();
    public isLoading = computed(() => this.loadingUsers.size === 0);

    public getCachedUserData(userId: string): UserCacheData | undefined {
        return this.userCache().get(userId);
    }

    public loadUserData(userId: string): Observable<UserCacheData | null> {
        const cachedData = this.userCache().get(userId);
        if (cachedData !== undefined) {
            return of(cachedData);
        }

        if (this.loadingUsers.has(userId)) {
            return this.waitForUserData(userId);
        }

        this.loadingUsers.add(userId);
        return forkJoin({
            photo: this.graphService
                .getPhotoForUser(userId, environment.apiConfig[1].scopes)
                .pipe(catchError(() => of(null))),
            userDetails: this.graphService.getUserDetails(userId).pipe(catchError(() => of(null)))
        }).pipe(
            map(({ photo, userDetails }) => {
                const userData: UserCacheData = {
                    photo,
                    userName: userDetails ? [userDetails.givenName, userDetails.surname].filter(Boolean).join(' ') : '',
                    orgCode: userDetails?.displayName ? (userDetails.displayName.match(/\((.*?)\)/)?.[1] ?? '') : ''
                };

                this.userCache.update(cache => {
                    cache.set(userId, userData);
                    return cache;
                });
                this.loadingUsers.delete(userId);
                return userData;
            }),
            catchError(() => {
                this.loadingUsers.delete(userId);
                return of(null);
            })
        );
    }

    private waitForUserData(userId: string): Observable<UserCacheData | null> {
        return interval(100).pipe(
            map(() => this.userCache().get(userId)),
            filter(userData => userData !== undefined),
            take(1),
            map(userData => userData ?? null)
        );
    }

    public loadUsersData(userIds: string[]): Observable<UserCacheData[]> {
        const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
        if (uniqueUserIds.length === 0) {
            return of([]);
        }

        return forkJoin(uniqueUserIds.map(userId => this.loadUserData(userId)));
    }
}
